// ProductSimplified.jsx - Updated to handle the actual response structure

import {
  Box,
  TextField,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import React, { useEffect, useState, useRef } from "react";
import {
  getRaccourcisByOrganisation,
  getProductByBarCode,
} from "../../Services/MenuApi";
import { getCategories } from "../../Services/CategrieApi";
import IngredientPopup from "./Ingredients";
import QuickAddProductModal from "./QuickAddProductModal";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ClientSelector from "./ClientSelector";
import { getImageUrl } from "../../Helpers/Helpers";

const ProductSimplified = ({
  onAddToCart,
  organisationId,
  selectedClient,
  onClientSelect,
  chargementProduits = false,
  onProductCreated,
  clientSearchCalcActive,
  clientSearchCalcBuffer,
  onActivateClientSearchCalc,
  onDeactivateClientSearchCalc,
  productSearchCalcActive,
  productSearchCalcBuffer,
  onActivateProductSearchCalc,
  onDeactivateProductSearchCalc,
}) => {
  const { t } = useTranslation();
  const [raccourciGroups, setRaccourciGroups] = useState([]);
  const [loadingRaccourcis, setLoadingRaccourcis] = useState(false);
  const [loadingBarcode, setLoadingBarcode] = useState(false);
  const [ingredientPopupOpen, setIngredientPopupOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [currentProductIngredients, setCurrentProductIngredients] = useState(
    [],
  );
  const [selectedIngredients, setSelectedIngredients] = useState([]);

  // Quick-add-product state (offered when a scanned barcode matches nothing)
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [pendingBarcode, setPendingBarcode] = useState("");
  const [quickAddCategories, setQuickAddCategories] = useState([]);

  // Barcode input state
  const [barcodeInput, setBarcodeInput] = useState("");
  const barcodeInputRef = useRef(null);

  const timeoutRef = useRef(null);
  const lastKeyPressTime = useRef(0);

  useEffect(() => {
    if (organisationId) {
      loadRaccourciGrids();
    }
  }, [organisationId]);

  const loadRaccourciGrids = async () => {
    try {
      setLoadingRaccourcis(true);
      const response = await getRaccourcisByOrganisation(organisationId);

      console.log("Raccourcis response:", response);

      if (response.success && response.data) {
        // The response has a data array where each item has raccourci_name and items
        const raccourciData = response.data?.data || [];

        if (Array.isArray(raccourciData) && raccourciData.length > 0) {
          // Transform the data into a more usable format
          const transformedGroups = raccourciData.map((group) => ({
            name: group.raccourci_name || "Raccourcis",
            products: (group.items || []).map((item) => {
              // The product might be in item.product_id or directly in item
              const product = item.product_id || item.product || item;
              return {
                _id: product._id || item.product_id?._id,
                titre: product.titre || { fr: item.libelle || "Produit" },
                libelle: item.libelle || product.libelle || product.titre?.fr,
                prix_ttc: product.prix_ttc || 0,
                image: product.image || item.image || [],
                ingredients: product.ingredients || [],
                dispo: product.dispo !== false,
              };
            }),
          }));

          setRaccourciGroups(transformedGroups);
          console.log("Transformed groups:", transformedGroups);
        } else {
          // If the data is already in the correct format (array of groups)
          setRaccourciGroups(raccourciData);
        }
      } else {
        console.warn("No raccourcis data received");
        setRaccourciGroups([]);
      }
    } catch (error) {
      console.error("Error loading raccourci grids:", error);
      toast.error(t("Error loading shortcuts"));
      setRaccourciGroups([]);
    } finally {
      setLoadingRaccourcis(false);
    }
  };

  // Shared by native typing and the on-screen keyboard binding below.
  const processBarcodeValue = (rawValue) => {
    const cleanedValue = rawValue.replace(/\n/g, "").trim();
    setBarcodeInput(rawValue);

    const now = Date.now();
    const timeDiff = now - lastKeyPressTime.current;
    lastKeyPressTime.current = now;

    // Detect if it's a barcode scanner (fast input)
    const isScannerInput = timeDiff < 50 && rawValue.length > 3;

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout
    timeoutRef.current = setTimeout(
      () => {
        if (cleanedValue !== "") {
          handleBarcodeSearch(cleanedValue);
        }
      },
      isScannerInput ? 100 : 500,
    );
  };

  // Handle barcode input
  const handleBarcodeInput = (e) => processBarcodeValue(e.target.value);

  // Mirror the shared on-screen keyboard's buffer into this input while
  // it's bound here, so typing on it drives the same barcode search as
  // native input.
  useEffect(() => {
    if (productSearchCalcActive) {
      processBarcodeValue(productSearchCalcBuffer || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productSearchCalcBuffer, productSearchCalcActive]);

  // Add a found (or just-created) product to the cart, or open the ingredient popup
  const addFoundProduct = (product, { silent = false } = {}) => {
    if (product.ingredients && product.ingredients.length > 0) {
      setCurrentProduct(product);
      setCurrentProductIngredients(product.ingredients);
      setSelectedIngredients([]);
      setIngredientPopupOpen(true);
    } else {
      onAddToCart({
        id: product._id || product.id,
        name: product.titre?.fr || product.libelle,
        price: product.prix_ttc,
        ingredients: [],
        productInfo: product,
      });
      if (!silent) toast.success(t("Product added successfully"));
    }
  };

  // No product matches this barcode — offer to create it on the spot
  const offerToCreateProduct = async (barcode) => {
    const result = await Swal.fire({
      title: t("Produit introuvable"),
      text: t("Aucun produit ne correspond à ce code-barres. Voulez-vous en créer un ?"),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: t("Oui, créer"),
      cancelButtonText: t("Annuler"),
    });
    if (result.isConfirmed) {
      if (!quickAddCategories.length) {
        try {
          const res = await getCategories(organisationId);
          if (res?.data?.success) setQuickAddCategories(res.data.data || []);
        } catch (error) {
          console.error("Error loading categories:", error);
        }
      }
      setPendingBarcode(barcode);
      setQuickAddOpen(true);
    }
  };

  const handleProductCreated = (product) => {
    setQuickAddOpen(false);
    // Skip the "added to cart" toast — the creation modal already confirmed success
    addFoundProduct(product, { silent: true });
    setBarcodeInput("");
    if (onProductCreated) onProductCreated();
  };

  const handleBarcodeSearch = async (barcode) => {
    if (!barcode) return;

    setLoadingBarcode(true);

    try {
      const result = await getProductByBarCode({
        barcode: barcode,
        organisation: organisationId,
      });

      if (result.data.success && result.data.data) {
        addFoundProduct(result.data.data);

        // Clear input after successful add
        setBarcodeInput("");
      } else {
        setBarcodeInput("");
        await offerToCreateProduct(barcode);
      }
    } catch (error) {
      console.error("Error searching by barcode:", error);
      toast.error(t("Error searching for product"));
    } finally {
      setLoadingBarcode(false);
    }
  };

  const handleAddToCart = (menu) => {
    // If product has ingredients → open popup
    if (menu.ingredients && menu.ingredients.length > 0) {
      setCurrentProduct(menu);
      setCurrentProductIngredients(menu.ingredients);
      setSelectedIngredients([]);
      setIngredientPopupOpen(true);
      return;
    }

    // No ingredients → direct add
    onAddToCart({
      id: menu._id,
      name: menu.titre?.fr || menu.libelle,
      price: menu.prix_ttc,
      ingredients: [],
      productInfo: menu,
    });
  };

  // Handle Enter key press on barcode input
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      const value = e.target.value.trim();
      if (value) {
        handleBarcodeSearch(value);
      }
    }
  };

  return (
    <div
      className="content-wrapper pt-1 no-min-height"
      style={{
        margin: 0,
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 90px)",
      }}
    >
      <div
        className="container-full"
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Client Selector */}
        <div style={{ padding: "0 5px 10px 5px", flexShrink: 0 }}>
          <ClientSelector
            organisation={organisationId}
            selectedClient={selectedClient}
            onClientSelect={onClientSelect}
            calcActive={clientSearchCalcActive}
            searchBuffer={clientSearchCalcBuffer}
            onActivateCalc={onActivateClientSearchCalc}
            onDeactivateCalc={onDeactivateClientSearchCalc}
          />
        </div>

        {/* Barcode Input - Prominent and always visible */}
        <div style={{ padding: "0 5px 15px 5px", flexShrink: 0 }}>
          <TextField
            fullWidth
            size="medium"
            placeholder={t("Scan or enter barcode...")}
            value={barcodeInput}
            onChange={handleBarcodeInput}
            onKeyPress={handleKeyPress}
            onClick={() => {
              if (onActivateProductSearchCalc) onActivateProductSearchCalc(barcodeInput);
            }}
            inputRef={barcodeInputRef}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <i
                    className="fa fa-barcode"
                    style={{ fontSize: "20px", opacity: 0.7 }}
                  />
                </InputAdornment>
              ),
              endAdornment: loadingBarcode ? (
                <InputAdornment position="end">
                  <CircularProgress size={24} />
                </InputAdornment>
              ) : barcodeInput ? (
                <InputAdornment position="end">
                  <i
                    className="fa fa-times"
                    style={{
                      fontSize: "16px",
                      opacity: 0.7,
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setBarcodeInput("");
                      if (onDeactivateProductSearchCalc) onDeactivateProductSearchCalc();
                    }}
                  />
                </InputAdornment>
              ) : null,
              sx: {
                borderRadius: "8px",
                backgroundColor: productSearchCalcActive ? "#fff3ec" : "#f8f9fa",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: productSearchCalcActive ? "#fd683e" : "#e0e0e0",
                  borderWidth: "2px",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: productSearchCalcActive ? "#fd683e" : "#1976d2",
                },
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor: productSearchCalcActive ? "#fd683e" : "#1976d2",
                    borderWidth: "2px",
                  },
              },
            }}
          />
        </div>

        {/* Raccourcis Grid */}
        <Box
          sx={{
            flex: 1,
            padding: "5px",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {loadingRaccourcis ? (
            <div className="text-center" style={{ padding: "40px" }}>
              <Spinner animation="border" role="status" />
              <p className="mt-2">{t("Loading shortcuts...")}</p>
            </div>
          ) : raccourciGroups.length === 0 ? (
            <div className="text-center" style={{ padding: "40px" }}>
              <div style={{ color: "#6c757d", fontSize: "16px" }}>
                <i
                  className="fa fa-star fa-2x mb-3"
                  style={{ opacity: 0.5 }}
                ></i>
                <p>
                  {t("No shortcuts configured. Scan barcode to add products.")}
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Display each raccourci group */}
              {raccourciGroups.map((raccourciGroup, groupIndex) => (
                <div
                  key={`group-${groupIndex}`}
                  style={{ marginBottom: "20px" }}
                >
                  <h6
                    style={{
                      margin: "0 0 8px 4px",
                      fontSize: "14px",
                      fontWeight: "bold",
                      color: "#495057",
                      borderBottom: "2px solid #e9ecef",
                      paddingBottom: "4px",
                    }}
                  >
                    <i
                      className="fa fa-star"
                      style={{ color: "#ffc107", marginRight: "6px" }}
                    />
                    {raccourciGroup.name}
                  </h6>
                  <div className="row gx-2 gy-2">
                    {raccourciGroup.products &&
                    raccourciGroup.products.length > 0 ? (
                      raccourciGroup.products.map((menu, index) => (
                        <div
                          key={menu._id || index}
                          className="col-6 col-sm-4 col-md-3 col-lg-2"
                          style={{ padding: "4px" }}
                        >
                          <Box
                            sx={{
                              cursor: "pointer",
                              borderRadius: 2,
                              overflow: "hidden",
                              boxShadow: 1,
                              transition: "transform 0.2s, box-shadow 0.2s",
                              "&:hover": {
                                transform: "translateY(-4px)",
                                boxShadow: 4,
                              },
                            }}
                            onClick={() => {
                              console.log(menu)
                              handleAddToCart(menu);
                            }}
                          >
                            <Box
                              sx={{
                                width: "100%",
                                height: 70,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "#f0f0f0",
                              }}
                            >
                              <img
                                src={getImageUrl(menu.image)}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "images/product/no-image.png";
                                }}
                                alt={menu.titre?.fr || menu.libelle}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "contain",
                                }}
                              />
                            </Box>
                            <Box sx={{ textAlign: "center", p: 0.5 }}>
                              <p
                                className="m-0"
                                style={{
                                  fontSize: "0.8rem",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  fontWeight: "500",
                                }}
                              >
                                {menu.titre?.fr || menu.libelle}
                              </p>
                              <small
                                style={{
                                  color: "#28a745",
                                  fontSize: "0.75rem",
                                  fontWeight: "600",
                                }}
                              >
                                {menu.prix_ttc?.toFixed(3) || "0.000"} TND
                              </small>
                            </Box>
                          </Box>
                        </div>
                      ))
                    ) : (
                      <div
                        className="col-12 text-center"
                        style={{ padding: "10px" }}
                      >
                        <small className="text-muted">
                          {t("No products in this group")}
                        </small>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}
        </Box>
      </div>

      {/* Ingredient Popup */}
      <IngredientPopup
        open={ingredientPopupOpen}
        product={currentProduct}
        ingredients={currentProductIngredients}
        onClose={() => setIngredientPopupOpen(false)}
        onConfirm={(selectedIngredients) => {
          onAddToCart({
            id: currentProduct._id || currentProduct.id,
            name: currentProduct.titre?.fr,
            price: currentProduct.prix_ttc,
            ingredients: selectedIngredients,
            productInfo: currentProduct,
          });
          setIngredientPopupOpen(false);
        }}
      />

      <QuickAddProductModal
        open={quickAddOpen}
        barcode={pendingBarcode}
        categories={quickAddCategories}
        organisationId={organisationId}
        onClose={() => setQuickAddOpen(false)}
        onCreated={handleProductCreated}
      />
    </div>
  );
};

export default ProductSimplified;
